/**
 * Query Processing Utility for Enhanced Multi-keyword Search
 * Processes search queries to extract movie titles, languages, actors, directors, etc.
 */

import { LanguageDetector } from './languageDetector';

export enum SearchIntent {
  MOVIE_WITH_LANGUAGE = 'movie_with_language',
  ACTOR_IN_GENRE = 'actor_in_genre',
  DIRECTOR_MOVIES = 'director_movies',
  GENRE_BY_YEAR = 'genre_by_year',
  MIXED_QUERY = 'mixed_query',
  SIMPLE_TITLE = 'simple_title',
  // New explicit intent types
  FILMOGRAPHY = 'filmography',           // "Leonardo DiCaprio movies"
  BEST_OF = 'best_of',                   // "best horror 2024"
  SIMILAR_TO = 'similar_to',             // "movies like Inception"
  ACTOR_FILMOGRAPHY = 'actor_filmography', // "movies with Tom Hanks"
  DIRECTOR_FILMOGRAPHY = 'director_filmography', // "films by Christopher Nolan"
  RECENT_GENRE = 'recent_genre',         // "new action movies"
  AWARD_WINNING = 'award_winning',       // "oscar winning movies"
  STREAMING_PLATFORM = 'streaming_platform', // "netflix originals"
  DECADE_GENRE = 'decade_genre'          // "90s action movies"
}

// Intent pattern definitions for query classification
export interface IntentPattern {
  pattern: RegExp;
  intent: SearchIntent;
  extractors: {
    name?: number;  // Capture group index for name
    genre?: number;
    year?: number;
    platform?: number;
    title?: number;
  };
}

// Intent patterns ordered by specificity (most specific first)
export const INTENT_PATTERNS: IntentPattern[] = [
  // Filmography patterns
  {
    pattern: /^(.+?)\s+(movies?|films?|filmography)$/i,
    intent: SearchIntent.FILMOGRAPHY,
    extractors: { name: 1 }
  },
  {
    pattern: /^(movies?|films?)\s+(with|starring|featuring)\s+(.+)$/i,
    intent: SearchIntent.ACTOR_FILMOGRAPHY,
    extractors: { name: 3 }
  },
  {
    pattern: /^(movies?|films?)\s+(by|directed by|from)\s+(.+)$/i,
    intent: SearchIntent.DIRECTOR_FILMOGRAPHY,
    extractors: { name: 3 }
  },
  // Similar/like patterns
  {
    pattern: /^(movies?|films?)\s+(like|similar to)\s+(.+)$/i,
    intent: SearchIntent.SIMILAR_TO,
    extractors: { title: 3 }
  },
  {
    pattern: /^(like|similar to)\s+(.+)$/i,
    intent: SearchIntent.SIMILAR_TO,
    extractors: { title: 2 }
  },
  // Best of patterns
  {
    pattern: /^best\s+(.+?)\s+(movies?|films?)?\s*(\d{4})?$/i,
    intent: SearchIntent.BEST_OF,
    extractors: { genre: 1, year: 3 }
  },
  {
    pattern: /^top\s+(\d+)?\s*(.+?)\s+(movies?|films?)?$/i,
    intent: SearchIntent.BEST_OF,
    extractors: { genre: 2 }
  },
  // Recent/new patterns
  {
    pattern: /^(new|recent|latest)\s+(.+?)\s*(movies?|films?)?$/i,
    intent: SearchIntent.RECENT_GENRE,
    extractors: { genre: 2 }
  },
  // Decade patterns
  {
    pattern: /^(\d{2})s\s+(.+?)\s*(movies?|films?)?$/i,
    intent: SearchIntent.DECADE_GENRE,
    extractors: { year: 1, genre: 2 }
  },
  {
    pattern: /^(.+?)\s+(from|of)\s+the\s+(\d{2})s$/i,
    intent: SearchIntent.DECADE_GENRE,
    extractors: { genre: 1, year: 3 }
  },
  // Award patterns
  {
    pattern: /^(oscar|academy award|golden globe)\s+(winning|winner|nominated)?\s*(movies?|films?)?$/i,
    intent: SearchIntent.AWARD_WINNING,
    extractors: {}
  },
  // Streaming platform patterns
  {
    pattern: /^(netflix|amazon|hulu|disney\+?|hbo|apple tv\+?)\s+(originals?|movies?|shows?)?$/i,
    intent: SearchIntent.STREAMING_PLATFORM,
    extractors: { platform: 1 }
  }
];

export interface IntentMatch {
  intent: SearchIntent;
  confidence: number;
  extractedData: {
    name?: string;
    genre?: string;
    year?: string;
    platform?: string;
    title?: string;
    decade?: string;
  };
}

export interface EnhancedQueryAnalysis {
  // Enhanced from existing QueryAnalysis
  title: string;
  actors: string[];
  directors: string[];
  genre: string;
  year: string;
  language: string;
  productionCompany: string;
  keywords: string[];
  queryType: string;
  confidence: number;

  // New enhanced fields
  languages: string[];           // Multiple languages: ["Malayalam", "Hindi", "English"]
  combinedTerms: string[];      // ["Moonwalkers", "Malayalam"]
  searchIntent: SearchIntent;   // "movie_with_language", "actor_in_genre", etc.
  movieTitle?: string;          // Extracted likely movie title
  isLanguageSpecific: boolean;  // Whether query contains language information
  intentMatch?: IntentMatch;    // Detailed intent match with extracted data
}

export interface QuotedStringResult {
  quotedStrings: string[];
  remainingQuery: string;
  hasQuotes: boolean;
}

export class QueryProcessor {

  /**
   * Extract quoted strings from a query for exact title matching
   * Example: 'movies like "The Dark Knight"' → { quotedStrings: ["The Dark Knight"], remainingQuery: "movies like" }
   */
  static extractQuotedStrings(query: string): QuotedStringResult {
    const quotedStrings: string[] = [];
    const quotedPattern = /"([^"]+)"|'([^']+)'/g;
    let match: RegExpExecArray | null;

    while ((match = quotedPattern.exec(query)) !== null) {
      const extracted = match[1] || match[2];
      if (extracted && extracted.trim()) {
        quotedStrings.push(extracted.trim());
      }
    }

    // Remove quoted strings from query
    const remainingQuery = query
      .replace(quotedPattern, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (quotedStrings.length > 0) {
      console.log('📝 QueryProcessor: Extracted quoted strings:', quotedStrings, 'remaining:', remainingQuery);
    }

    return {
      quotedStrings,
      remainingQuery,
      hasQuotes: quotedStrings.length > 0
    };
  }

  /**
   * Detect if query looks like a multi-word movie title
   * Returns the title if detected, null otherwise
   */
  static detectMultiWordTitle(query: string): string | null {
    const trimmed = query.trim();

    // Common patterns that indicate this is NOT a title search
    const nonTitlePatterns = [
      /\b(movies?|films?)\s+(by|with|like|similar|starring|featuring)/i,
      /^(best|top|new|recent|latest)\s+/i,
      /\s+(movies?|films?)$/i,
      /\b(actor|director|genre|year)\b/i
    ];

    for (const pattern of nonTitlePatterns) {
      if (pattern.test(trimmed)) {
        return null;
      }
    }

    // If query has 2-5 words with proper capitalization, likely a title
    const words = trimmed.split(/\s+/);
    if (words.length >= 2 && words.length <= 6) {
      // Check if it looks like a title (starts with capital or all lowercase)
      const looksLikeTitle = words.every(word =>
        // Allow capitalized words, numbers, small articles
        /^[A-Z]/.test(word) ||
        /^\d+$/.test(word) ||
        ['the', 'a', 'an', 'of', 'and', 'or', 'to', 'in', 'for', 'on', 'at', 'by', 'with'].includes(word.toLowerCase())
      ) || words.every(word => /^[a-z]/.test(word)); // All lowercase also acceptable

      if (looksLikeTitle) {
        console.log('📝 QueryProcessor: Detected multi-word title:', trimmed);
        return trimmed;
      }
    }

    return null;
  }

  /**
   * Detect search intent from query using pattern matching
   */
  static detectIntent(query: string): IntentMatch | null {
    const normalizedQuery = query.trim();

    for (const { pattern, intent, extractors } of INTENT_PATTERNS) {
      const match = normalizedQuery.match(pattern);
      if (match) {
        const extractedData: IntentMatch['extractedData'] = {};

        // Extract data from capture groups
        if (extractors.name && match[extractors.name]) {
          extractedData.name = match[extractors.name].trim();
        }
        if (extractors.genre && match[extractors.genre]) {
          extractedData.genre = match[extractors.genre].trim();
        }
        if (extractors.year && match[extractors.year]) {
          const yearStr = match[extractors.year].trim();
          // Convert 2-digit decade to decade string
          if (yearStr.length === 2) {
            extractedData.decade = yearStr;
            extractedData.year = yearStr.startsWith('0') ? `20${yearStr}` : `19${yearStr}`;
          } else {
            extractedData.year = yearStr;
          }
        }
        if (extractors.platform && match[extractors.platform]) {
          extractedData.platform = match[extractors.platform].trim();
        }
        if (extractors.title && match[extractors.title]) {
          extractedData.title = match[extractors.title].trim();
        }

        console.log('🎯 QueryProcessor: Detected intent:', intent, 'from query:', normalizedQuery, 'extracted:', extractedData);

        return {
          intent,
          confidence: 0.9,
          extractedData
        };
      }
    }

    return null;
  }

  /**
   * Enhanced query analysis that builds on existing tmdbApi analyzeQuery method
   */
  static enhanceQueryAnalysis(existingAnalysis: any, query: string): EnhancedQueryAnalysis {
    // First, extract any quoted strings (exact title matches)
    const quotedResult = this.extractQuotedStrings(query);
    const queryForAnalysis = quotedResult.hasQuotes ? quotedResult.remainingQuery : query;

    const languageAnalysis = LanguageDetector.analyzeQueryIntent(queryForAnalysis);

    // Try explicit intent pattern matching on the non-quoted part
    const intentMatch = this.detectIntent(queryForAnalysis);

    // Check for multi-word title if no other intent detected
    const multiWordTitle = !intentMatch && !quotedResult.hasQuotes
      ? this.detectMultiWordTitle(query)
      : null;

    // Determine search intent based on query characteristics
    let searchIntent = SearchIntent.SIMPLE_TITLE;

    if (quotedResult.hasQuotes) {
      // Quoted strings indicate exact title search
      searchIntent = SearchIntent.SIMPLE_TITLE;
    } else if (intentMatch) {
      // Use detected intent from pattern matching
      searchIntent = intentMatch.intent;
    } else if (languageAnalysis.isLanguageSpecific && languageAnalysis.movieTitle) {
      searchIntent = SearchIntent.MOVIE_WITH_LANGUAGE;
    } else if (existingAnalysis.actors.length > 0 && existingAnalysis.genre) {
      searchIntent = SearchIntent.ACTOR_IN_GENRE;
    } else if (existingAnalysis.directors.length > 0) {
      searchIntent = SearchIntent.DIRECTOR_MOVIES;
    } else if (existingAnalysis.genre && existingAnalysis.year) {
      searchIntent = SearchIntent.GENRE_BY_YEAR;
    } else if (languageAnalysis.languages.length > 0 || existingAnalysis.actors.length > 0 || existingAnalysis.genre) {
      searchIntent = SearchIntent.MIXED_QUERY;
    }

    // Extract combined search terms - include quoted strings as primary terms
    const combinedTerms = [...quotedResult.quotedStrings, ...languageAnalysis.nonLanguageTerms];
    if (languageAnalysis.languages.length > 0) {
      combinedTerms.push(...languageAnalysis.languages.map(code => 
        LanguageDetector.mapCodeToLanguage(code) || code
      ));
    }
    const uniqueTerms = [...new Set(combinedTerms)];
    
    // Calculate enhanced confidence
    let enhancedConfidence = existingAnalysis.confidence || 0.5;
    enhancedConfidence += languageAnalysis.confidence * 0.3; // Language detection adds confidence
    enhancedConfidence = Math.min(1.0, enhancedConfidence);
    
    // Merge intent match data with existing analysis
    // Priority: quoted strings > intent match > multi-word title > existing analysis
    const mergedTitle = quotedResult.quotedStrings[0] ||
      intentMatch?.extractedData.title ||
      intentMatch?.extractedData.name ||
      multiWordTitle ||
      existingAnalysis.title ||
      languageAnalysis.movieTitle || '';

    const mergedGenre = intentMatch?.extractedData.genre ||
      existingAnalysis.genre ||
      LanguageDetector.getGenreHintFromLanguage(query) || '';

    const mergedYear = intentMatch?.extractedData.year ||
      existingAnalysis.year || '';

    // Boost confidence for explicit matches
    if (quotedResult.hasQuotes) {
      enhancedConfidence = Math.min(1.0, enhancedConfidence + 0.3); // Quoted = very confident
    } else if (intentMatch) {
      enhancedConfidence = Math.min(1.0, enhancedConfidence + 0.2);
    } else if (multiWordTitle) {
      enhancedConfidence = Math.min(1.0, enhancedConfidence + 0.1);
    }

    return {
      // Preserve existing analysis with merged intent data
      title: mergedTitle,
      actors: intentMatch?.extractedData.name && searchIntent === SearchIntent.FILMOGRAPHY
        ? [intentMatch.extractedData.name, ...existingAnalysis.actors]
        : existingAnalysis.actors || [],
      directors: intentMatch?.extractedData.name && searchIntent === SearchIntent.DIRECTOR_FILMOGRAPHY
        ? [intentMatch.extractedData.name, ...existingAnalysis.directors]
        : existingAnalysis.directors || [],
      genre: mergedGenre,
      year: mergedYear,
      language: existingAnalysis.language || (languageAnalysis.languages.length > 0 ? languageAnalysis.languages[0] : ''),
      productionCompany: existingAnalysis.productionCompany || '',
      keywords: existingAnalysis.keywords || languageAnalysis.nonLanguageTerms,
      queryType: intentMatch ? intentMatch.intent : (existingAnalysis.queryType || 'enhanced'),
      confidence: enhancedConfidence,

      // Enhanced fields
      languages: languageAnalysis.languages,
      combinedTerms: uniqueTerms,
      searchIntent,
      movieTitle: quotedResult.quotedStrings[0] || intentMatch?.extractedData.title || multiWordTitle || languageAnalysis.movieTitle,
      isLanguageSpecific: languageAnalysis.isLanguageSpecific,
      intentMatch: intentMatch || undefined
    };
  }
  
  /**
   * Process query through intelligent pipeline as described in the plan
   * Example: "Moonwalkers Malayalam" -> tokenize -> classify -> generate search strategies
   */
  static processQueryPipeline(query: string): {
    tokens: string[];
    classification: { [key: string]: string[] };
    searchStrategies: SearchStrategy[];
    confidence: number;
  } {
    // Step 1: Tokenize
    const tokens = query.toLowerCase().trim().split(/\s+/).filter(token => token.length > 1);
    
    // Step 2: Classify tokens
    const classification: { [key: string]: string[] } = {
      titles: [],
      languages: [],
      actors: [],
      directors: [],
      genres: [],
      years: [],
      other: []
    };
    
    // Language detection
    const detectedLanguages = LanguageDetector.detectLanguages(query);
    classification.languages = detectedLanguages;
    
    // Extract non-language terms as potential titles/actors
    const nonLanguageTerms = LanguageDetector.extractNonLanguageTerms(query);
    
    // Simple heuristics for classification
    tokens.forEach(token => {
      // Check if it's a year
      if (/^\d{4}$/.test(token) && parseInt(token) >= 1900 && parseInt(token) <= new Date().getFullYear() + 2) {
        classification.years.push(token);
      }
      // Check if it's a known genre
      else if (this.isGenre(token)) {
        classification.genres.push(token);
      }
      // Check if it's a language
      else if (LanguageDetector.mapLanguageToCode(token)) {
        // Already handled above
      }
      // Otherwise, treat as potential title/actor/director
      else if (nonLanguageTerms.includes(token)) {
        classification.titles.push(token);
      }
    });
    
    // If we have multiple non-language terms, the first ones are likely the title
    if (nonLanguageTerms.length > 1) {
      classification.titles = nonLanguageTerms.slice(0, Math.ceil(nonLanguageTerms.length / 2));
      // Remaining could be actors/directors (basic heuristic)
      const remaining = nonLanguageTerms.slice(Math.ceil(nonLanguageTerms.length / 2));
      classification.actors.push(...remaining);
    }
    
    // Step 3: Generate search strategies
    const searchStrategies = this.generateSearchStrategies(classification, query);
    
    // Step 4: Calculate confidence
    const confidence = this.calculatePipelineConfidence(classification, tokens.length);
    
    return {
      tokens,
      classification,
      searchStrategies,
      confidence
    };
  }
  
  /**
   * Generate search strategies based on classification
   */
  private static generateSearchStrategies(classification: { [key: string]: string[] }, originalQuery: string): SearchStrategy[] {
    const strategies: SearchStrategy[] = [];
    
    // Strategy 1: Title + Language search
    if (classification.titles.length > 0 && classification.languages.length > 0) {
      const title = classification.titles.join(' ');
      classification.languages.forEach(lang => {
        strategies.push({
          type: 'title_with_language',
          query: title,
          language: lang,
          priority: 10,
          confidence: 0.9
        });
      });
    }
    
    // Strategy 2: Combined exact search
    strategies.push({
      type: 'exact_query',
      query: originalQuery,
      priority: 8,
      confidence: 0.7
    });
    
    // Strategy 3: Title-only search
    if (classification.titles.length > 0) {
      strategies.push({
        type: 'title_only',
        query: classification.titles.join(' '),
        priority: 7,
        confidence: 0.8
      });
    }
    
    // Strategy 4: Language + Genre search
    if (classification.languages.length > 0 && classification.genres.length > 0) {
      classification.languages.forEach(lang => {
        classification.genres.forEach(genre => {
          strategies.push({
            type: 'language_genre',
            language: lang,
            genre: genre,
            priority: 6,
            confidence: 0.7
          });
        });
      });
    }
    
    // Strategy 5: Actor search
    if (classification.actors.length > 0) {
      classification.actors.forEach(actor => {
        strategies.push({
          type: 'actor',
          query: actor,
          priority: 5,
          confidence: 0.6
        });
      });
    }
    
    return strategies.sort((a, b) => b.priority - a.priority);
  }
  
  /**
   * Calculate confidence score for pipeline processing
   */
  private static calculatePipelineConfidence(classification: { [key: string]: string[] }, totalTokens: number): number {
    let confidence = 0.3; // Base confidence
    
    // Language detection adds confidence
    if (classification.languages.length === 1) {
      confidence += 0.3;
    } else if (classification.languages.length > 1) {
      confidence += 0.2;
    }
    
    // Having clear title terms adds confidence
    if (classification.titles.length > 0) {
      confidence += 0.2;
    }
    
    // Having structured data (year, genre) adds confidence
    if (classification.years.length > 0) confidence += 0.1;
    if (classification.genres.length > 0) confidence += 0.1;
    
    // Penalize if too many unclassified tokens
    const classifiedTokens = Object.values(classification).flat().length;
    const unclassifiedRatio = (totalTokens - classifiedTokens) / totalTokens;
    confidence -= unclassifiedRatio * 0.2;
    
    return Math.max(0.1, Math.min(1.0, confidence));
  }
  
  /**
   * Check if a token is a known genre
   */
  private static isGenre(token: string): boolean {
    const genres = [
      'action', 'adventure', 'animation', 'comedy', 'crime', 'documentary',
      'drama', 'family', 'fantasy', 'history', 'horror', 'music', 'mystery',
      'romance', 'science', 'fiction', 'thriller', 'war', 'western', 'musical',
      'biographical', 'superhero', 'noir', 'indie', 'arthouse'
    ];
    
    return genres.includes(token.toLowerCase());
  }
  
  /**
   * Generate search suggestions based on processed query
   */
  static generateSearchSuggestions(query: string): string[] {
    const pipeline = this.processQueryPipeline(query);
    const suggestions: string[] = [];
    
    // Add language-based suggestions
    const languageSuggestions = LanguageDetector.generateLanguageSearchSuggestions(query);
    suggestions.push(...languageSuggestions);
    
    // Add classification-based suggestions
    if (pipeline.classification.titles.length > 0) {
      const title = pipeline.classification.titles.join(' ');
      
      // Add year variations
      if (pipeline.classification.years.length === 0) {
        const currentYear = new Date().getFullYear();
        for (let i = 0; i < 3; i++) {
          suggestions.push(`${title} ${currentYear - i}`);
        }
      }
      
      // Add genre variations
      if (pipeline.classification.genres.length === 0) {
        ['action', 'drama', 'comedy', 'thriller'].forEach(genre => {
          suggestions.push(`${title} ${genre}`);
        });
      }
    }
    
    return [...new Set(suggestions)].slice(0, 8); // Remove duplicates and limit
  }
}

// Define SearchStrategy interface for compatibility
interface SearchStrategy {
  type: string;
  query?: string;
  language?: string;
  genre?: string;
  priority: number;
  confidence: number;
}

export default QueryProcessor;
